<template>
    <div class="vehicles">
        <div class="flex dashboard-wrapper">
            <div class="w-[15%] bg-slate-100">
                <SidebarDashboard/>
            </div>

            <div class="w-[85%]">
                <Header></Header>

                <div class="dashboard-content p-5 px-10 pb-20 min-h-screen bg-[#f5f5f5]">
                    <!-- Page Header -->
                    <div class="flex items-center justify-between mb-6">
                        <div>
                            <h2 class="font-bold text-3xl text-gray-800">{{ currentTitle }}</h2>
                            <p class="text-gray-500 text-sm mt-1">Gerencie todos os veículos cadastrados</p>
                        </div>
                        <button
                            class="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-xl shadow transition-colors"
                            @click="openNewVehicleDialog"
                        >
                            <i class="pi pi-plus text-sm"></i>
                            Cadastrar Veículo
                        </button>
                    </div>

                    <!-- Vehicles Table -->
                    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                        <DataTable
                            ref="dt"
                            :value="allVehicles"
                            :loading="loadingVehicles"
                            :paginator="true"
                            :rows="10"
                            :rowsPerPageOptions="[5, 10, 25, 50]"
                            :filters="filters"
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            currentPageReportTemplate="Exibindo {first} a {last} de {totalRecords} veículos"
                            sortMode="multiple"
                            class="text-sm"
                            stripedRows
                        >
                            <template #header>
                                <div class="flex flex-wrap gap-3 items-center justify-between">
                                    <span class="text-base font-semibold text-gray-700">
                                        <i class="pi pi-car mr-2 text-blue-600"></i>
                                        Todos os Veículos
                                    </span>
                                    <div class="flex gap-2 items-center">
                                        <span class="p-input-icon-left">
                                            <i class="pi pi-search" />
                                            <InputText
                                                v-model="filters['global'].value"
                                                placeholder="Buscar veículo, placa, cliente..."
                                                class="bg-white p-2.5 pl-10 border rounded-lg text-sm w-[300px]"
                                            />
                                        </span>
                                        <button
                                            class="p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 transition"
                                            title="Recarregar lista"
                                            @click="loadVehicles"
                                        >
                                            <i class="pi pi-refresh"></i>
                                        </button>
                                    </div>
                                </div>
                            </template>

                            <template #empty>
                                <div class="flex flex-col items-center justify-center py-12 text-gray-400">
                                    <i class="pi pi-car text-5xl mb-3"></i>
                                    <p class="font-medium">Nenhum veículo encontrado</p>
                                    <p class="text-sm mt-1">Cadastre um novo veículo clicando no botão acima.</p>
                                </div>
                            </template>

                            <template #loading>
                                <div class="flex items-center justify-center py-12 text-blue-500">
                                    <i class="pi pi-spin pi-spinner text-2xl mr-3"></i>
                                    Carregando veículos...
                                </div>
                            </template>

                            <!-- Customer -->
                            <Column field="customer_name" header="Cliente" sortable>
                                <template #body="slotProps">
                                    <div>
                                        <p class="font-semibold text-gray-800">{{ slotProps.data.customer_name || '-' }}</p>
                                        <p class="text-xs text-gray-400">{{ slotProps.data.customer_document || '' }}</p>
                                    </div>
                                </template>
                            </Column>

                            <!-- Vehicle -->
                            <Column field="model" header="Veículo" sortable>
                                <template #body="slotProps">
                                    <div class="flex items-center gap-2">
                                        <div class="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
                                            <i :class="getVehicleIcon(slotProps.data.vehicle_type)"></i>
                                        </div>
                                        <div>
                                            <p class="font-medium text-gray-800">{{ slotProps.data.model }}</p>
                                            <p class="text-xs text-gray-400">{{ slotProps.data.brand }} · {{ slotProps.data.year }}</p>
                                        </div>
                                    </div>
                                </template>
                            </Column>

                            <!-- Plate -->
                            <Column field="plate" header="Placa" sortable>
                                <template #body="slotProps">
                                    <span class="bg-gray-100 text-gray-700 font-bold text-xs px-2.5 py-1 rounded font-mono tracking-widest">
                                        {{ slotProps.data.plate }}
                                    </span>
                                </template>
                            </Column>

                            <!-- Type -->
                            <Column field="vehicle_type" header="Tipo" sortable>
                                <template #body="slotProps">
                                    <span class="bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full">
                                        {{ slotProps.data.vehicle_type }}
                                    </span>
                                </template>
                            </Column>

                            <!-- Color -->
                            <Column field="color" header="Cor" sortable></Column>

                            <!-- IMEI -->
                            <Column field="imei" header="IMEI">
                                <template #body="slotProps">
                                    <span class="text-xs font-mono text-gray-500">{{ slotProps.data.imei || '-' }}</span>
                                </template>
                            </Column>

                            <!-- Equipment Rate -->
                            <Column field="equipment_rate" header="Taxa Equipamento">
                                <template #body="slotProps">
                                    <span v-if="slotProps.data.equipment_rate" class="text-green-600 font-semibold text-sm">
                                        {{ formatCurrency(slotProps.data.equipment_rate) }}
                                    </span>
                                    <span v-else class="text-gray-300 text-xs">—</span>
                                </template>
                            </Column>

                            <!-- Created At -->
                            <Column field="created_at" header="Cadastrado em" sortable>
                                <template #body="slotProps">
                                    <span class="text-xs text-gray-500">{{ formatDate(slotProps.data.created_at) }}</span>
                                </template>
                            </Column>
                        </DataTable>
                    </div>
                </div>

                <Footer></Footer>
            </div>
        </div>

        <!-- ====== New Vehicle Dialog - Step 1: Select Customer ====== -->
        <Dialog
            v-model:visible="showStep1"
            :style="{ width: '500px' }"
            header="Cadastrar Novo Veículo"
            :modal="true"
            class="p-fluid"
        >
            <div class="py-2">
                <p class="text-gray-500 text-sm mb-5">
                    <i class="pi pi-info-circle mr-1 text-blue-500"></i>
                    Selecione o cliente ao qual o veículo será vinculado.
                </p>
                <label class="font-bold block mb-2 text-sm text-gray-700">Cliente</label>
                <Dropdown
                    v-model="vehicle.customer_id"
                    :options="customers"
                    filter
                    optionLabel="name"
                    optionValue="customer_id"
                    placeholder="Pesquise e selecione um cliente..."
                    class="w-full border"
                    :showClear="true"
                >
                    <template #option="slotProps">
                        <div>
                            <p class="font-semibold text-gray-800 text-sm">{{ slotProps.option.name }}</p>
                            <p class="text-xs text-gray-400">{{ slotProps.option.document }}</p>
                        </div>
                    </template>
                </Dropdown>
            </div>
            <template #footer>
                <Button label="Cancelar" icon="pi pi-times" class="p-2 px-4 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-lg mr-2" @click="showStep1 = false" />
                <Button
                    label="Próximo"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    class="p-2 px-4 bg-blue-600 text-white hover:bg-blue-700 rounded-lg"
                    :disabled="!vehicle.customer_id"
                    @click="proceedToStep2"
                />
            </template>
        </Dialog>

        <!-- ====== New Vehicle Dialog - Step 2: Vehicle Form ====== -->
        <Dialog
            v-model:visible="showStep2"
            :style="{ width: '860px' }"
            :modal="true"
            class="p-fluid"
        >
            <template #header>
                <div class="flex items-center gap-3">
                    <div class="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                        <i class="pi pi-car text-blue-600"></i>
                    </div>
                    <div>
                        <p class="font-bold text-gray-800">Dados do Veículo</p>
                        <p class="text-xs text-gray-400">Cliente: <span class="text-blue-600 font-medium">{{ selectedCustomerName }}</span></p>
                    </div>
                </div>
            </template>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-2">
                <!-- Modelo -->
                <div class="field">
                    <label class="font-semibold block mb-1.5 text-sm text-gray-700">Modelo <span class="text-red-400">*</span></label>
                    <InputText placeholder="Ex: Corolla" class="p-2.5 w-full border rounded-lg" v-model="vehicle.model" />
                </div>

                <!-- Marca -->
                <div class="field">
                    <label class="font-semibold block mb-1.5 text-sm text-gray-700">Marca <span class="text-red-400">*</span></label>
                    <Dropdown
                        v-model="vehicle.brand"
                        :options="vehicleBrands"
                        placeholder="Selecione a Marca"
                        class="w-full border"
                    />
                </div>

                <!-- Tipo -->
                <div class="field">
                    <label class="font-semibold block mb-1.5 text-sm text-gray-700">Tipo de Veículo <span class="text-red-400">*</span></label>
                    <Dropdown
                        v-model="vehicle.vehicle_type"
                        :options="vehicleTypes"
                        placeholder="Selecione o Tipo"
                        class="w-full border"
                    />
                </div>

                <!-- Ano -->
                <div class="field">
                    <label class="font-semibold block mb-1.5 text-sm text-gray-700">Ano <span class="text-red-400">*</span></label>
                    <InputText placeholder="Ex: 2023" class="p-2.5 w-full border rounded-lg" v-model="vehicle.year" />
                </div>

                <!-- Placa -->
                <div class="field">
                    <label class="font-semibold block mb-1.5 text-sm text-gray-700">Placa <span class="text-red-400">*</span></label>
                    <InputText placeholder="Ex: ABC-1234" class="p-2.5 w-full border rounded-lg" v-model="vehicle.plate" />
                </div>

                <!-- Cor -->
                <div class="field">
                    <label class="font-semibold block mb-1.5 text-sm text-gray-700">Cor <span class="text-red-400">*</span></label>
                    <InputText placeholder="Ex: Prata" class="p-2.5 w-full border rounded-lg" v-model="vehicle.color" />
                </div>

                <!-- RENAVAM -->
                <div class="field">
                    <label class="font-semibold block mb-1.5 text-sm text-gray-700">RENAVAM <span class="text-red-400">*</span></label>
                    <InputText placeholder="RENAVAM" class="p-2.5 w-full border rounded-lg" v-model="vehicle.renavam" />
                </div>

                <!-- IMEI -->
                <div class="field">
                    <label class="font-semibold block mb-1.5 text-sm text-gray-700">IMEI</label>
                    <InputText placeholder="IMEI do rastreador" class="p-2.5 w-full border rounded-lg" v-model="vehicle.imei" />
                </div>

                <!-- SIM CARD -->
                <div class="field">
                    <label class="font-semibold block mb-1.5 text-sm text-gray-700">SIM CARD</label>
                    <InputText placeholder="Número do SIM Card" class="p-2.5 w-full border rounded-lg" v-model="vehicle.sim_card" />
                </div>

                <!-- Operadora -->
                <div class="field">
                    <label class="font-semibold block mb-1.5 text-sm text-gray-700">Operadora</label>
                    <InputText placeholder="Ex: Claro, Vivo..." class="p-2.5 w-full border rounded-lg" v-model="vehicle.telephone_operator" />
                </div>

                <!-- Marca Equipamento -->
                <div class="field">
                    <label class="font-semibold block mb-1.5 text-sm text-gray-700">Marca do Equipamento</label>
                    <InputText placeholder="Marca do rastreador" class="p-2.5 w-full border rounded-lg" v-model="vehicle.equipment_brand" />
                </div>

                <!-- Modelo Equipamento -->
                <div class="field">
                    <label class="font-semibold block mb-1.5 text-sm text-gray-700">Modelo do Equipamento</label>
                    <InputText placeholder="Modelo do rastreador" class="p-2.5 w-full border rounded-lg" v-model="vehicle.equipment_model" />
                </div>

                <!-- Garantia -->
                <div class="field">
                    <label class="font-semibold block mb-1.5 text-sm text-gray-700">Garantia do Equipamento</label>
                    <InputText placeholder="Ex: 12 meses" class="p-2.5 w-full border rounded-lg" v-model="vehicle.equipment_warranty" />
                </div>
            </div>

            <hr class="my-4">

            <!-- Taxas -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Taxa do Equipamento -->
                <div class="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <label class="font-semibold block mb-2 text-sm text-gray-700">Taxa do Equipamento (R$)</label>
                    <InputText v-model="vehicle.equipment_rate" placeholder="0,00" class="p-2.5 w-full border rounded-lg mb-3 bg-white" />
                    <div class="flex items-center gap-2">
                        <Checkbox v-model="vehicle.active_equipment_rate" inputId="aeq" :binary="true" />
                        <label for="aeq" class="text-sm text-gray-600 cursor-pointer">Ativar taxa do equipamento</label>
                    </div>
                </div>

                <!-- Taxa de Ativação -->
                <div class="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <label class="font-semibold block mb-2 text-sm text-gray-700">Taxa de Ativação (R$)</label>
                    <InputText v-model="vehicle.activation_rate" placeholder="0,00" class="p-2.5 w-full border rounded-lg mb-3 bg-white" />
                    <div class="flex flex-col gap-2">
                        <div class="flex items-center gap-2">
                            <Checkbox v-model="vehicle.active_activation_rate" inputId="aac" :binary="true" />
                            <label for="aac" class="text-sm text-gray-600 cursor-pointer">Ativar taxa de ativação</label>
                        </div>
                        <div class="flex items-center gap-2">
                            <Checkbox v-model="vehicle.automatic_activation_rate" inputId="aauto" :binary="true" />
                            <label for="aauto" class="text-sm text-gray-600 cursor-pointer">Ativar automaticamente</label>
                        </div>
                    </div>
                </div>
            </div>

            <template #footer>
                <Button
                    label="Voltar"
                    icon="pi pi-arrow-left"
                    class="p-2 px-4 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-lg mr-2"
                    @click="backToStep1"
                />
                <Button
                    label="Salvar Veículo"
                    icon="pi pi-check"
                    class="p-2 px-4 bg-green-500 text-white hover:bg-green-600 rounded-lg"
                    :loading="savingVehicle"
                    @click="saveVehicle"
                />
            </template>
        </Dialog>
    </div>
</template>

<script>
import SidebarDashboard from '@/components/Dashboard/SidebarDashboard.vue'
import Header from '@/components/Dashboard/HeaderDashboard.vue'
import Footer from '@/components/Dashboard/FooterDashboard.vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import Dropdown from 'primevue/dropdown'
import Checkbox from 'primevue/checkbox'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import { FilterMatchMode } from 'primevue/api'

import customersService from '@/services/customersService'
import vehiclesService from '@/services/vehiclesService'
import configurationsService from '@/services/configurationsService'
import dateService from '@/services/dateService'

export default {
    name: 'DashboardVehicles',
    components: {
        SidebarDashboard, Header, Footer,
        DataTable, Column, InputText, Dropdown, Checkbox, Button, Dialog
    },
    data() {
        return {
            currentTitle: '',
            customers: [],
            allVehicles: [],
            vehicleBrands: [],
            vehicleTypes: [],
            loadingVehicles: false,
            savingVehicle: false,
            showStep1: false,
            showStep2: false,
            filters: {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS }
            },
            vehicle: this.initialStateVehicle()
        }
    },
    computed: {
        selectedCustomerName() {
            if (!this.vehicle.customer_id) return ''
            const found = this.customers.find(c => c.customer_id === this.vehicle.customer_id)
            return found ? found.name : ''
        }
    },
    methods: {
        initialStateVehicle() {
            return {
                customer_id: null,
                model: null,
                year: null,
                plate: null,
                color: null,
                renavam: null,
                vehicle_type: null,
                brand: null,
                equipment_brand: null,
                equipment_model: null,
                equipment_warranty: null,
                imei: null,
                sim_card: null,
                telephone_operator: null,
                equipment_rate: null,
                active_equipment_rate: false,
                activation_rate: null,
                active_activation_rate: false,
                automatic_activation_rate: false
            }
        },

        openNewVehicleDialog() {
            this.vehicle = this.initialStateVehicle()
            this.showStep1 = true
        },

        proceedToStep2() {
            if (!this.vehicle.customer_id) {
                this.$swal.fire('Selecione um cliente', 'É necessário selecionar um cliente para continuar.', 'warning')
                return
            }
            this.showStep1 = false
            this.showStep2 = true
        },

        backToStep1() {
            this.showStep2 = false
            this.showStep1 = true
        },

        async loadVehicles() {
            this.loadingVehicles = true
            try {
                const response = await vehiclesService.getAllAdmin()
                this.allVehicles = response.data || []
            } catch (e) {
                console.error('Erro ao carregar veículos:', e)
                this.$swal.fire('Erro', 'Não foi possível carregar os veículos.', 'error')
            } finally {
                this.loadingVehicles = false
            }
        },

        saveVehicle() {
            const required = ['model', 'year', 'plate', 'color', 'renavam', 'vehicle_type', 'brand']
            const missing = required.filter(f => !this.vehicle[f])

            if (missing.length > 0) {
                this.$swal.fire('Campos obrigatórios', 'Preencha todos os campos marcados com *.', 'warning')
                return
            }

            this.savingVehicle = true

            vehiclesService.newVehicle(this.vehicle).then((response) => {
                if (response.data && response.data.status === 200) {
                    this.$swal.fire('Veículo cadastrado!', 'O veículo foi cadastrado com sucesso.', 'success')
                    this.showStep2 = false
                    this.vehicle = this.initialStateVehicle()
                    this.loadVehicles()
                } else {
                    this.$swal.fire('Erro ao criar veículo', response.data?.message || 'Tente novamente.', 'error')
                }
            }).catch(e => {
                console.error(e)
                this.$swal.fire('Erro', 'Erro de conexão com o servidor.', 'error')
            }).finally(() => {
                this.savingVehicle = false
            })
        },

        getVehicleIcon(type) {
            const icons = {
                'Motocicleta': 'fa-solid fa-motorcycle',
                'Passeio': 'pi pi-car',
                'Caminhão': 'fa-solid fa-truck',
                'Caminhonete': 'fa-solid fa-truck-pickup',
            }
            return icons[type] || 'pi pi-car'
        },

        formatCurrency(value) {
            return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        },

        formatDate(date) {
            if (!date) return '-'
            return dateService.formatBR(date)
        }
    },

    async mounted() {
        this.currentTitle = this.$route.meta.title
        this.$watch('$route', () => {
            this.currentTitle = this.$route.meta.title
        })

        const customers = await customersService.getLocalCustomers()
        this.customers = customers

        configurationsService.getAllConfigurations().then((response) => {
            response.data.map((index) => {
                switch (index.configuration_key) {
                    case 'vehicle_brands':
                        this.vehicleBrands = JSON.parse(index.configuration_value)
                        break
                    case 'vehicle_types':
                        this.vehicleTypes = JSON.parse(index.configuration_value)
                        break
                }
            })
        })

        this.loadVehicles()
    }
}
</script>

<style lang="scss" scoped>
.dashboard-wrapper {
    min-height: 100vh;
}
</style>